import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import { Header } from '../../components/Header/Header';
import { TotalScore } from '../../components/TotalScore/TotalScore';
import { CategoryCard } from '../../components/CategoryCard/CategoryCard';
import { LiftModal } from '../../components/LiftModal/LiftModal';
import { ResultDisplay } from '../../components/ResultDisplay/ResultDisplay';
import { WhatIfModal } from './WhatIfModal';
import { useUser } from '../../app/UserContext';
import { getExercisesByCategory, getCompetitionLift } from '../../domain/scoring/exerciseCatalog';
import { CATEGORY_ORDER, COMPETITION_LIFT_NAMES } from '../../domain/scoring/categories';
import { scoreWorkout } from '../../domain/scoring/scoreWorkout';
import { overridesReady } from '../../app/exerciseOverrides';
import {
  createWorkout,
  getMonthlyBests,
  getAllUserWorkouts,
} from '../../infrastructure/repositories/workoutRepository';
import { getCurrentMonthKey } from '../../domain/workouts/types';
import { getLeaderboardEntries } from '../../infrastructure/repositories/leaderboardRepository';
import { buildLeaderboard, getPreviousMonthKey, TOTAL_SCOPE } from '../../domain/leaderboard/types';
import type { Category, ExerciseConfig } from '../../domain/scoring/types';
import type { MonthlySummary } from '../../domain/workouts/types';
import type { LastEntries } from '../../components/LiftModal/LiftModal';
import {
  buildLiftHighlights,
  foldLiftIntoHighlights,
  buildCategoryRecords,
  foldLiftIntoCategoryRecords,
  type LiftHighlightMap,
  type CategoryRecordMap,
} from '../../domain/workouts/liftHighlights';
import { awardBadges, type FeedBadges } from '../../domain/leaderboard/badges';
import { invalidateAwardsData } from '../awards/useAwardsData';
import './LogPage.css';

interface ResultData {
  exerciseName: string;
  enteredWeight: number;
  reps: number;
  estimated1Rm: number;
  normalized1Rm: number;
  competitionLiftName: string;
  points: number;
  isNewCategoryBest: boolean;
  previousBestPoints?: number;
  monthlyTotalBefore: number;
  monthlyTotalAfter: number;
  category: Category;
  badges: FeedBadges;
}

export function LogPage() {
  const navigate = useNavigate();
  const { user } = useUser();

  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [monthlySummary, setMonthlySummary] = useState<MonthlySummary | null>(null);
  const [prevMonthlySummary, setPrevMonthlySummary] = useState<MonthlySummary | null>(null);
  const [userRank, setUserRank] = useState<number | undefined>(undefined);
  const [resultData, setResultData] = useState<ResultData | null>(null);
  const [isResultOpen, setIsResultOpen] = useState(false);
  const [isWhatIfOpen, setIsWhatIfOpen] = useState(false);
  /**
   * The last weight and reps logged per exercise. Loaded once here rather than in
   * each modal, so the lift form and the what-if calculator share one read.
   */
  const [lastEntries, setLastEntries] = useState<LastEntries>({});
  /** Each exercise's PR and month best, off the same history read. */
  const [highlights, setHighlights] = useState<LiftHighlightMap>({});
  /** Each category's best points, for judging awards the moment a lift lands. */
  const [categoryRecords, setCategoryRecords] = useState<CategoryRecordMap>({});
  const [lastUsedExercise, setLastUsedExercise] = useState<Record<Category, string | undefined>>({
    push: undefined,
    row: undefined,
    hinge: undefined,
    squat: undefined,
  });

  // Load data on mount and when user changes
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    async function load() {
      try {
        const monthKey = getCurrentMonthKey();
        const prevMonthKey = getPreviousMonthKey(monthKey);

        const [summary, prevSummary, rawLeaderboard, allWorkouts] = await Promise.all([
          getMonthlyBests(user!.id, monthKey),
          getMonthlyBests(user!.id, prevMonthKey),
          getLeaderboardEntries(monthKey),
          getAllUserWorkouts(user!.id),
        ]);

        if (!cancelled) {
          setMonthlySummary(summary);
          setPrevMonthlySummary(prevSummary);

          // Newest first, so the first hit per exercise is the most recent.
          const seeds: LastEntries = {};
          for (const w of allWorkouts) {
            if (seeds[w.exerciseId] === undefined) {
              seeds[w.exerciseId] = { weightLb: w.enteredWeightLb, reps: w.reps };
            }
          }
          setLastEntries(seeds);
          setHighlights(buildLiftHighlights(allWorkouts, monthKey));
          setCategoryRecords(buildCategoryRecords(allWorkouts, monthKey));

          const sorted = buildLeaderboard(rawLeaderboard, 'points', TOTAL_SCOPE);
          const myEntry = sorted.find((e) => e.userId === user!.id);
          setUserRank(myEntry?.rank);
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  function handleCategoryTap(category: Category) {
    setSelectedCategory(category);
    setIsModalOpen(true);
  }

  function handleModalClose() {
    setIsModalOpen(false);
    setSelectedCategory(null);
  }

  async function handleLiftSubmit(data: {
    exerciseId: string;
    weightLb: number;
    reps: number;
    workoutDate: string;
    note: string;
  }) {
    if (!user || !selectedCategory) return;

    setIsSubmitting(true);

    try {
      // Never score against a stale multiplier: if the admin's overrides are
      // still loading, wait for them before reading the catalog.
      await overridesReady();

      const exercises = getExercisesByCategory(selectedCategory);
      const exercise = exercises.find((e) => e.id === data.exerciseId) as ExerciseConfig;
      const competitionLift = getCompetitionLift(selectedCategory);

      // Calculate the score preview locally
      const scoreResult = scoreWorkout({
        enteredWeightLb: data.weightLb,
        reps: data.reps,
        inputWeightMultiplier: exercise.inputWeightMultiplier,
        normalizationMultiplier: exercise.normalizationMultiplier,
        bodyWeightFactor: exercise.bodyWeightFactor,
        bodyWeightLb: user.bodyWeightLb,
        scoringSex: user.scoringSex,
        category: selectedCategory,
      });

      // Get monthly totals before submission
      const totalBefore = monthlySummary?.totalPoints ?? 0;
      const previousBestPoints = getCategoryBestPoints(selectedCategory);

      // Awards, judged against the records as they stood a moment ago. Same
      // function the feed uses, so the confirmation screen and the feed cannot
      // disagree about what this lift earned.
      const liftMonthKey = data.workoutDate.slice(0, 7);
      const priorCategory = categoryRecords[selectedCategory] ?? {};
      const badges = awardBadges(
        {
          exerciseId: data.exerciseId,
          points: scoreResult.points,
          estimated1RmLb: scoreResult.estimated1RmLb,
        },
        {
          categoryPoints: priorCategory.bestPoints,
          categoryMonthPoints:
            liftMonthKey === getCurrentMonthKey() ? priorCategory.bestMonthPoints : undefined,
          exercise1Rm: highlights[data.exerciseId]?.pr?.estimated1RmLb,
        },
      );

      // Save to Firestore
      await createWorkout({
        userId: user.id,
        exerciseId: data.exerciseId,
        exerciseName: exercise.displayName,
        category: selectedCategory,
        enteredWeightLb: data.weightLb,
        reps: data.reps,
        inputWeightMultiplier: exercise.inputWeightMultiplier,
        normalizationMultiplier: exercise.normalizationMultiplier,
        bodyWeightFactor: exercise.bodyWeightFactor,
        workoutDate: data.workoutDate,
        bodyWeightLb: user.bodyWeightLb,
        scoringSex: user.scoringSex,
        note: data.note || undefined,
        exerciseConfigVersion: 'v1',
      });

      // The feed, the award view and the trophy case all read one cached copy
      // of the log; this lift has just made it stale.
      invalidateAwardsData();

      // Remember last used exercise, and seed next time's form from this lift so
      // the default is right without waiting for a refetch.
      setLastUsedExercise((prev) => ({
        ...prev,
        [selectedCategory]: data.exerciseId,
      }));
      setLastEntries((prev) => ({
        ...prev,
        [data.exerciseId]: { weightLb: data.weightLb, reps: data.reps },
      }));
      // Same reason: the records shown under the form should include the lift
      // just logged, without waiting for a refetch.
      setHighlights((prev) =>
        foldLiftIntoHighlights(
          prev,
          data.exerciseId,
          {
            workoutDate: data.workoutDate,
            monthKey: data.workoutDate.slice(0, 7),
            enteredWeightLb: data.weightLb,
            reps: data.reps,
            estimated1RmLb: scoreResult.estimated1RmLb,
            points: scoreResult.points,
            inputWeightMultiplier: exercise.inputWeightMultiplier,
            bodyWeightFactor: exercise.bodyWeightFactor,
          },
          getCurrentMonthKey(),
        ),
      );
      setCategoryRecords((prev) =>
        foldLiftIntoCategoryRecords(
          prev,
          selectedCategory,
          scoreResult.points,
          liftMonthKey,
          getCurrentMonthKey(),
        ),
      );

      // Fetch fresh monthly summary after save
      const monthKey = getCurrentMonthKey();
      const freshSummary = await getMonthlyBests(user.id, monthKey);
      setMonthlySummary(freshSummary);

      const totalAfter = freshSummary.totalPoints;
      // Beating the month's category best is exactly what the two category
      // badges mean, so take it from them rather than recomputing it against a
      // summary that is only ever about the current month - a backdated lift
      // must not be announced as improving this month's total.
      const isNewBest = badges.isCategoryPr || badges.isCategoryMonthBest;

      // Prepare result display
      setResultData({
        exerciseName: exercise.displayName,
        enteredWeight: data.weightLb,
        reps: data.reps,
        estimated1Rm: Math.round(scoreResult.estimated1RmLb),
        normalized1Rm: Math.round(scoreResult.normalized1RmLb),
        competitionLiftName:
          competitionLift?.displayName ?? COMPETITION_LIFT_NAMES[selectedCategory],
        points: Math.round(scoreResult.points),
        isNewCategoryBest: isNewBest,
        category: selectedCategory,
        badges,
        previousBestPoints: previousBestPoints ? Math.round(previousBestPoints) : undefined,
        monthlyTotalBefore: Math.round(totalBefore),
        monthlyTotalAfter: Math.round(totalAfter),
      });

      setIsModalOpen(false);
      setIsResultOpen(true);
    } catch (err) {
      console.error('Error logging lift:', err);
      // TODO: Show error in modal
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleResultClose() {
    setIsResultOpen(false);
    setResultData(null);
    setSelectedCategory(null);
  }

  function handleProfileClick() {
    navigate('/settings');
  }

  function getCategoryBest(category: Category) {
    if (!monthlySummary) return null;
    const field = (
      { push: 'pushBest', row: 'rowBest', hinge: 'hingeBest', squat: 'squatBest' } as const
    )[category];
    return monthlySummary[field];
  }

  function getCategoryBestPoints(category: Category): number | undefined {
    return getCategoryBest(category)?.points;
  }

  const totalPoints = monthlySummary?.totalPoints ?? 0;
  const prevTotalPoints = prevMonthlySummary?.totalPoints;
  const delta =
    monthlySummary && prevMonthlySummary
      ? Math.round(totalPoints) - Math.round(prevTotalPoints ?? 0)
      : undefined;

  const hasAnyScore = monthlySummary && CATEGORY_ORDER.some((c) => getCategoryBest(c) !== null);

  // This month's best per category, so the calculator can flag which rows would
  // actually improve the score rather than just naming a number.
  const categoryBestPoints = Object.fromEntries(
    CATEGORY_ORDER.map((c) => [c, getCategoryBestPoints(c)]).filter(([, p]) => p !== undefined),
  ) as Partial<Record<Category, number>>;

  // While any modal is up the button is both useless and a liability: it lives in
  // layout flow whereas a modal overlay is position: fixed, so on iOS the two can
  // disagree by the height of the browser toolbar mid-transition and the button
  // appears over the modal. Not rendering it removes the coupling entirely.
  const isAnyModalOpen = isModalOpen || isResultOpen || isWhatIfOpen;

  return (
    <Layout
      overlay={
        isAnyModalOpen ? undefined : (
          <button
            className="log-page__whatif"
            type="button"
            onClick={() => setIsWhatIfOpen(true)}
            aria-label="Open the what-if points calculator"
          >
            <svg width="15" height="17" viewBox="0 0 15 17" aria-hidden="true">
              <rect
                x="1"
                y="1"
                width="13"
                height="15"
                rx="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
              />
              <rect x="3.4" y="3.4" width="8.2" height="3" rx="0.8" fill="currentColor" />
              <g fill="currentColor">
                <circle cx="4.6" cy="9.4" r="1" />
                <circle cx="7.5" cy="9.4" r="1" />
                <circle cx="10.4" cy="9.4" r="1" />
                <circle cx="4.6" cy="12.6" r="1" />
                <circle cx="7.5" cy="12.6" r="1" />
                <circle cx="10.4" cy="12.6" r="1" />
              </g>
            </svg>
            What if
          </button>
        )
      }
    >
      <div className="log-page">
        <Header onProfileClick={handleProfileClick} />

        <div className="log-page__content">
          <TotalScore
            total={hasAnyScore ? Math.round(totalPoints) : 0}
            delta={delta}
            rank={userRank}
            isFirstMonth={!hasAnyScore}
          />

          <div className="log-page__categories">
            {CATEGORY_ORDER.map((category) => {
              const best = getCategoryBest(category);
              return (
                <CategoryCard
                  key={category}
                  category={category}
                  points={best ? Math.round(best.points) : undefined}
                  normalizedMax={best ? `${Math.round(best.normalized1RmLb)} lb` : undefined}
                  bestExerciseName={best?.exerciseName}
                  competitionLift={COMPETITION_LIFT_NAMES[category]}
                  onClick={() => handleCategoryTap(category)}
                />
              );
            })}
          </div>

          {user && !hasAnyScore && (
            <p className="log-page__hint">
              Tap a category above to log your first lift, {user.displayName}!
            </p>
          )}
        </div>
      </div>

      {/* Lift Entry Modal */}
      {selectedCategory && (
        <LiftModal
          isOpen={isModalOpen}
          category={selectedCategory}
          onClose={handleModalClose}
          onSubmit={handleLiftSubmit}
          exercises={getExercisesByCategory(selectedCategory).filter((e) => e.isActive)}
          isSubmitting={isSubmitting}
          defaultExerciseId={lastUsedExercise[selectedCategory]}
          lastEntries={lastEntries}
          highlights={highlights}
        />
      )}

      {/* Result Display */}
      {resultData && (
        <ResultDisplay isOpen={isResultOpen} onClose={handleResultClose} result={resultData} />
      )}

      {/* "What if" points calculator - read-only, saves nothing */}
      {isWhatIfOpen && user && (
        <WhatIfModal
          onClose={() => setIsWhatIfOpen(false)}
          bodyWeightLb={user.bodyWeightLb}
          scoringSex={user.scoringSex}
          lastEntries={lastEntries}
          categoryBestPoints={categoryBestPoints}
          highlights={highlights}
        />
      )}
    </Layout>
  );
}
